import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react"
import {
  buildAudioInputs,
  getAudioInputPrefSnapshot,
  matchSavedInput,
  nextAudioInputPref,
  resolveAudioInputSelection,
  resolvePhysicalDeviceId,
  resolveSelectorValue,
  subscribeAudioInputPref,
  SYSTEM_AUDIO_INPUT_VALUE,
  writeAudioInputPref,
  type AudioInputDevice,
  type AudioInputList,
} from "@/lib/audio-inputs"
import type { AudioRecorderSource, UseAudioRecorderOptions } from "@/hooks/use-audio-recorder"

/** Chrome умеет стрелять devicechange пачкой — схлопываем. */
const DEVICE_REFRESH_DEBOUNCE_MS = 250

const getServerAudioInputPref = () => null

export type UseAudioInputPickerOptions = {
  /**
   * Разрешён ли пункт «звук вкладки/экрана». Надиктовке он не нужен: преф общий на
   * браузер, и системный вход, выбранный в таймлайне, читается тут как вход по умолчанию.
   */
  allowSystemAudio?: boolean
  /**
   * Выбранного устройства нет — пишем со входа по умолчанию. Сообщение показывает
   * вызывающий: где-то это тост, где-то плашка формы.
   */
  onDeviceUnavailable?: () => void
}

export type AudioInputPicker = {
  /** Реальные устройства текущего списка; до выдачи прав браузер его не раскрывает и он пуст. */
  devices: AudioInputDevice[]
  /** "" — вход по умолчанию, SYSTEM_AUDIO_INPUT_VALUE — звук вкладки, иначе deviceId. */
  value: string
  select: (next: string) => void
  /** Имя сохранённого устройства, которого ещё нет в списке (см. buildAudioInputOptions). */
  pendingLabel: string | null
  /** Устройство, которым подписывается пункт «по умолчанию»; null — ещё неизвестно. */
  defaultDeviceId: string | null
  source: AudioRecorderSource
  /** Колбэки useAudioRecorder: захват — единственный момент, когда браузер раскрывает список. */
  recorderProps: Required<
    Pick<UseAudioRecorderOptions, "resolveCapturedDevice" | "onCaptureStarted" | "onDeviceUnavailable">
  >
}

/**
 * Выбор аудиовхода для формы записи: список устройств, память о выборе и источник
 * для useAudioRecorder.
 *
 * Вся возня вокруг deviceId тут не от хорошей жизни: до успешного getUserMedia браузер
 * не раскрывает ни имена, ни идентификаторы устройств, Safari выдаёт новый deviceId на
 * каждую загрузку, а Chrome вправе перебить мягкое ограничение своим рейтингом микрофонов.
 * Поэтому список перечитывается и по devicechange, и по факту старта записи, а выбор
 * ищется по цепочке id → groupId → имя (см. matchSavedInput).
 *
 * Преф общий на вкладку: несколько пикеров (надиктовка агента, промпт шота, Rec на
 * таймлайне) подписаны на один снимок, а не держат каждый свой useState.
 */
export function useAudioInputPicker({
  allowSystemAudio = true,
  onDeviceUnavailable,
}: UseAudioInputPickerOptions = {}): AudioInputPicker {
  const [inputs, setInputs] = useState<AudioInputList>({ devices: [], systemDefaultDeviceId: null })
  const pref = useSyncExternalStore(subscribeAudioInputPref, getAudioInputPrefSnapshot, getServerAudioInputPref)
  const prefRef = useRef(pref)
  prefRef.current = pref
  /**
   * Устройство последнего захвата без ограничения по deviceId — им подписываем пункт
   * «по умолчанию» (у Chrome свой рейтинг микрофонов, и он не обязан совпадать с
   * системным). Захват конкретного устройства сюда не пишем: подпись стала бы врать.
   */
  const [capturedDefaultDeviceId, setCapturedDefaultDeviceId] = useState<string | null>(null)
  /** deviceId, выбранный пользователем из уже раскрытого списка (ограничение жёсткое). */
  const [pickedFromList, setPickedFromList] = useState<string | null>(null)
  const onDeviceUnavailableRef = useRef(onDeviceUnavailable)
  onDeviceUnavailableRef.current = onDeviceUnavailable

  const value = useMemo(
    () => resolveSelectorValue(pref, inputs.devices, allowSystemAudio),
    [pref, inputs.devices, allowSystemAudio],
  )
  const valueRef = useRef(value)
  valueRef.current = value

  const cancelledRef = useRef(false)
  useEffect(() => {
    cancelledRef.current = false
    return () => {
      cancelledRef.current = true
    }
  }, [])

  const refreshInputs = useCallback(() => {
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.enumerateDevices) return
    navigator.mediaDevices
      .enumerateDevices()
      .then((all) => {
        if (cancelledRef.current) return
        setInputs(buildAudioInputs(all))
      })
      .catch(() => {})
  }, [])

  // Имена и идентификаторы устройств браузер раскрывает только после успешного
  // getUserMedia, поэтому список перечитываем ещё и на старте записи (onCaptureStarted),
  // и по devicechange — когда микрофон воткнули или отключили.
  useEffect(() => {
    refreshInputs()
    if (typeof navigator === "undefined" || !navigator.mediaDevices?.addEventListener) return
    let debounce: ReturnType<typeof setTimeout> | null = null
    const onDeviceChange = () => {
      if (debounce != null) clearTimeout(debounce)
      debounce = setTimeout(refreshInputs, DEVICE_REFRESH_DEBOUNCE_MS)
    }
    navigator.mediaDevices.addEventListener("devicechange", onDeviceChange)
    return () => {
      if (debounce != null) clearTimeout(debounce)
      navigator.mediaDevices.removeEventListener("devicechange", onDeviceChange)
    }
  }, [refreshInputs])

  // Safari на каждую загрузку выдаёт новый deviceId — как только список раскрылся,
  // пишем актуальный id, чтобы остальные пикеры и следующий заход не шли по протухшему.
  useEffect(() => {
    if (pref?.kind !== "device") return
    const matched = matchSavedInput(pref, inputs.devices)
    if (matched && matched.deviceId !== pref.deviceId) {
      writeAudioInputPref({
        kind: "device",
        deviceId: matched.deviceId,
        groupId: matched.groupId,
        label: matched.label,
      })
    }
  }, [pref, inputs.devices])

  // Явный выбор из раскрытого списка: если преф сменили в другом пикере, и это
  // устройство у нас уже в списке — ограничение тоже должно стать жёстким.
  useEffect(() => {
    if (pref?.kind !== "device") {
      setPickedFromList(null)
      return
    }
    const matched = matchSavedInput(pref, inputs.devices)
    if (matched) setPickedFromList(matched.deviceId)
  }, [pref, inputs.devices])

  const fallbackToDefault = useCallback(() => {
    writeAudioInputPref(null)
    setPickedFromList(null)
    onDeviceUnavailableRef.current?.()
  }, [])

  const select = useCallback(
    (next: string) => {
      // Повторный клик по уже видимому пункту: иначе "" при спрятанном system-префе
      // стёр бы «звук вкладки», выбранный на таймлайне.
      if (next === valueRef.current) return
      setPickedFromList((prev) =>
        inputs.devices.some((d) => d.deviceId === next) ? next : prev === next ? prev : null,
      )
      writeAudioInputPref(nextAudioInputPref(next, inputs.devices, prefRef.current))
    },
    [inputs.devices],
  )

  /**
   * Права только что выданы: уточняем, с какого устройства писать. Возвращаем id,
   * если браузер отдал не то, что просил пользователь (мягкое ограничение он вправе
   * проигнорировать, а сохранённый id мог протухнуть), иначе null.
   */
  const resolveCapturedDevice = useCallback(
    (all: MediaDeviceInfo[], captured: string | null) => {
      // Пользователь сам выбрал вход по умолчанию — навязывать ему сохранённый микрофон
      // (и тем более ругаться на его отсутствие) не за что.
      const selected = valueRef.current
      if (!selected || selected === SYSTEM_AUDIO_INPUT_VALUE) return null
      const saved = prefRef.current
      if (saved?.kind !== "device") return null
      const list = buildAudioInputs(all)
      const wanted = matchSavedInput(saved, list.devices)
      if (!wanted) {
        // Сохранённого микрофона больше нет — пишем со входа по умолчанию и так же
        // показываем в селекторе, иначе он продолжит врать про отсутствующее устройство.
        fallbackToDefault()
        return null
      }
      // В Chrome захваченным может числиться сам алиас — сравниваем физические устройства.
      const capturedPhysical = resolvePhysicalDeviceId(captured, list.systemDefaultDeviceId)
      return wanted.deviceId === capturedPhysical ? null : wanted.deviceId
    },
    [fallbackToDefault],
  )

  const recorderProps = useMemo(
    () => ({
      resolveCapturedDevice,
      // Захват начался — права выданы, и браузер только теперь раскрывает имена устройств.
      onCaptureStarted: (captured: string | null) => {
        if (!valueRef.current) setCapturedDefaultDeviceId(captured)
        refreshInputs()
      },
      onDeviceUnavailable: fallbackToDefault,
    }),
    [fallbackToDefault, refreshInputs, resolveCapturedDevice],
  )

  const selection = resolveAudioInputSelection(value, inputs.devices)
  const source: AudioRecorderSource =
    selection.kind === "system"
      ? { kind: "system" }
      : selection.kind === "device"
        ? // Явный выбор из раскрытого списка остаётся жёстким, даже если браузер снова
          // спрятал устройства (список схлопнулся) — иначе он молча деградирует до мягкого.
          { kind: "microphone", deviceId: selection.deviceId, exact: selection.exact || pickedFromList === value }
        : { kind: "microphone", deviceId: null }

  return {
    devices: inputs.devices,
    value,
    select,
    pendingLabel: pref?.kind === "device" && value === pref.deviceId ? pref.label : null,
    defaultDeviceId:
      resolvePhysicalDeviceId(capturedDefaultDeviceId, inputs.systemDefaultDeviceId) ?? inputs.systemDefaultDeviceId,
    source,
    recorderProps,
  }
}
